/**
 * Terms of Service Page
 * Plain-language terms. Plan limits and prices here must match
 * workers/src/services/stripe.ts and frontend/src/data/landing-content.ts.
 */

import { Helmet } from 'react-helmet-async'

export function TermsPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Terms of Service — BankStatementReader</title>
        <meta name="description" content="The terms that govern your use of BankStatementReader: accounts, plans and billing, your content, acceptable use, and disclaimers." />
        <link rel="canonical" href="https://bankstatementreader.com/terms" />
      </Helmet>
      <div className="max-w-4xl mx-auto p-8 py-16">
        <h1 className="text-4xl font-bold mb-8 text-gray-900">Terms of Service</h1>

        <div className="bg-white rounded-lg shadow-sm p-8 space-y-8">
          <section>
            <p className="text-sm text-gray-600 mb-6">
              <strong>Last Updated:</strong> July 2, 2026
            </p>
            <p className="text-gray-700 leading-relaxed">
              These terms govern your use of BankStatementReader ("we", "us", the "service") at
              bankstatementreader.com. By using the service you agree to these terms. If you do not
              agree, do not use the service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-gray-900">The service</h2>
            <p className="text-gray-700 leading-relaxed">
              BankStatementReader converts bank statement PDFs you upload into structured data and
              exports (XLSX, CSV, JSON), and provides free browser-based utilities for working with
              statements and transaction files. How we handle your data is described in the{' '}
              <a href="/privacy" className="text-indigo-600 hover:text-indigo-800">Privacy Policy</a> and on the{' '}
              <a href="/security" className="text-indigo-600 hover:text-indigo-800">Security page</a>.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-gray-900">Accounts</h2>
            <p className="text-gray-700 leading-relaxed">
              Accounts are created through our authentication provider. You are responsible for the
              accuracy of your account information and for safeguarding access to your account.
              Activity under your account is your responsibility.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-gray-900">Plans, limits and billing</h2>
            <ul className="list-disc list-inside space-y-1 text-gray-700 mb-3">
              <li>The free tier currently includes 5 processed statements per month, no card required</li>
              <li>Paid plans are billed monthly through Stripe: Solo at $29/month (up to 100 statements/month), Starter at $79/month (up to 500 statements/month) and Growth at $199/month (up to 2,000 statements/month)</li>
              <li>A one-time Backlog Pass ($39 for 200 additional statements) can be purchased from the dashboard; its allowance is used after your monthly plan allowance and does not renew</li>
              <li>You can manage or cancel your subscription at any time through the billing portal; cancellation stops future charges</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              Prices and limits may change; changes apply from your next billing period and the
              current prices are always shown on the pricing page.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-gray-900">Your content</h2>
            <p className="text-gray-700 leading-relaxed">
              You retain all rights to the documents you upload. You grant us a limited license to
              store and process them solely to provide the service, which includes transmitting
              statement page images to our AI extraction provider as described in the Privacy Policy.
              You confirm you have the right to upload and process every document you submit. You can
              delete your statements at any time, which removes them from our storage.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-gray-900">Acceptable use</h2>
            <p className="mb-3 text-gray-700 leading-relaxed">You agree not to:</p>
            <ul className="list-disc list-inside space-y-1 text-gray-700">
              <li>Use the service for any unlawful purpose</li>
              <li>Upload documents you do not have the right to process</li>
              <li>Use the service to create, alter, or misrepresent financial documents, including for loan, rental, or visa applications</li>
              <li>Probe, disrupt, overload, or circumvent the service or its usage limits</li>
              <li>Resell the service without our written agreement</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-gray-900">Accuracy disclaimer</h2>
            <div className="bg-indigo-50 border border-indigo-200 rounded p-4">
              <p className="text-indigo-900 leading-relaxed">
                Extraction is automated and can contain errors, especially on low-quality scans.
                Always review the output and verify totals against the original statement before
                relying on it for accounting, tax, lending, or any other decision. The service is
                provided "as is" and "as available", without warranties of any kind to the extent
                permitted by law.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-gray-900">Limitation of liability</h2>
            <p className="text-gray-700 leading-relaxed">
              To the maximum extent permitted by law, we are not liable for indirect, incidental,
              special, or consequential damages, or for losses resulting from decisions made using
              extracted data. Our total liability for any claim is limited to the amount you paid us
              in the 12 months before the claim arose.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-gray-900">Termination</h2>
            <p className="text-gray-700 leading-relaxed">
              You may stop using the service and delete your account at any time. We may suspend or
              terminate accounts that violate these terms. Sections that by their nature should
              survive termination (your content rights, disclaimers, liability limits) survive it.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-gray-900">Changes and contact</h2>
            <p className="text-gray-700 leading-relaxed">
              We may update these terms; material changes will be reflected by the date above.
              Questions go to{' '}
              <a href="mailto:support@bankstatementreader.com" className="text-indigo-600 hover:text-indigo-800">
                support@bankstatementreader.com
              </a>.
            </p>
          </section>

          <div className="pt-4 border-t border-gray-200">
            <a href="/" className="text-indigo-600 hover:text-indigo-800 font-medium">
              ← Back to Home
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}
